<template>
  <import-banner
    :documents="allDocumentValues"
    :title="bannerTitle"
    :subtitle="bannerSubtitle"
    :ui-state="uiState"
    :is-expanded="bannerIsExpanded"
    @import-banner:clicked="handleSave"
    @import-document:delete="handleDelete"
  >
    <upload-section
      v-for="section in sectionOrder"
      :key="section"
      :section="section"
      @delete="handleDelete"
      @upload="handleUploadClick"
    ></upload-section>
  </import-banner>
</template>

<script setup lang="ts">
import { UIState } from '../types'
import type { AIOCRProposedDocuments } from '@app/shared/types/sf-data'

import { storeToRefs } from 'pinia'
import { computed, onBeforeUnmount, watch } from 'vue'

import ImportBanner from './import-banner.vue'
import UploadSection from './upload-section.vue'
import { useDocumentUpload } from '../composables/use-document-upload'
import { generateOCRDocumentsPayload } from '../utils'
import { saveSectionableOCRDocuments } from '@app/application/ai_ocr_documents/api'
import useI18n from '@app/shared/vue/composables/use-I18n'

import { useOCRSections } from '../store/use-ocr-sections'
import { useOCRDocuments } from '../store/use-ocr-documents'
import { useOCRUI } from '../store/use-ocr-ui'

// eslint-disable-next-line no-undef
defineOptions({
  name: 'AIOCRDocumentsUploaderPerSection',
})

interface Props {
  ledgerId: number
  proposedDocuments: AIOCRProposedDocuments
  reconciliationTextId: number
  bannerIsExpanded: boolean
}

const props = defineProps<Props>()

const I18n = useI18n()

const documentStore = useOCRDocuments()
const sectionStore = useOCRSections()
const uIStore = useOCRUI()

const {
  allDocumentsLength,
  allDocumentValues,
  nonProposedDocuments,
  documents,
} = storeToRefs(documentStore)

const { sections, sectionOrder } = storeToRefs(sectionStore)

const { uiState } = storeToRefs(uIStore)

const normalizedDocuments = sectionStore.receiveSections(
  props.proposedDocuments
)
documentStore.receiveDocuments(normalizedDocuments)

onBeforeUnmount(() => {
  removeSubscription()
})

const {
  subscribeToUploadProgress,
  removeSubscription,
  userManagedDocuments,
  triggerDocumentUpload,
  handleError,
} = useDocumentUpload()

const bannerTitle = I18n.t('ai_ocr_documents.banner.title')

const bannerSubtitle = computed(() => {
  const count = allDocumentsLength.value
  if (nonProposedDocuments.value) {
    return I18n.t('ai_ocr_documents.banner.subtitle_changed', { count })
  }
  if (count === 0) {
    return I18n.t('ai_ocr_documents.banner.subtitle_empty')
  }
  return I18n.t('ai_ocr_documents.banner.subtitle', { count })
})

async function handleUploadClick(section: string): Promise<void> {
  uIStore.transitionTo(UIState.Loading)
  sectionStore.activateSection(section)

  try {
    await triggerDocumentUpload()
    uIStore.transitionTo(UIState.Initial)
  } catch (e) {
    handleError('Error uploading document', e)
  }
}

async function handleSave(): Promise<void> {
  uIStore.transitionTo(UIState.Loading)

  const payload = generateOCRDocumentsPayload(sections.value, documents.value)

  try {
    const response = await saveSectionableOCRDocuments({
      ledgerId: props.ledgerId,
      reconciliationTextId: props.reconciliationTextId,
      payload,
    })

    if (!response?.channel || (response?.lastId !== 0 && !response?.lastId)) {
      throw new Error('Invalid response')
    }

    uIStore.transitionTo(UIState.WaitingForMB)

    subscribeToUploadProgress(response.channel, response.lastId)
  } catch (e) {
    handleError('Error saving documents', e)
  }
}

function handleDelete(documentId: string | number) {
  const canDeleteDocument = sectionStore.deleteDocumentFromSection(documentId)

  if (canDeleteDocument) {
    documentStore.deleteDocument(documentId.toString())
  }
}

watch(userManagedDocuments, async (newValue) => {
  if (newValue.length > 0) {
    documentStore.addDocuments(newValue)
    sectionStore.addSectionsPerDocuments(newValue)
  }
})
</script>
